import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Phone, MapPin, ExternalLink, Mail, Inbox, CheckCircle2, Copy, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const SUPPORT_EMAIL = "nowproductions15@gmail.com";

export default function StudentInbox() {
  const [items, setItems] = useState([]);

  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`).catch(() => {});
    load();
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch { toast.error("Couldn't copy"); }
  };

  const unread = items.filter((n) => !n.read).length;

  return (
    <section className="mt-12">
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Inbox</div>
          <h2 className="font-display font-black text-3xl tracking-tighter mt-1 flex items-center gap-3">
            <Inbox size={26} className="text-orange-800" />
            Your booking confirmations
            {unread > 0 && (
              <span className="text-xs font-bold bg-amber-600 text-white px-2.5 py-1 rounded-full">
                {unread} new
              </span>
            )}
          </h2>
        </div>
      </div>

      {items.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500 text-sm">
          Your inbox is empty. Once you complete a booking, the hostel address and owner contact will appear here.
        </div>
      )}

      <div className="space-y-4">
        {items.map((n) => (
          <div
            key={n.id}
            data-testid={`inbox-${n.id}`}
            className={`bg-white border rounded-2xl p-5 ${n.read ? "border-slate-200" : "border-orange-400 shadow-sm"}`}
          >
            <div className="flex flex-col md:flex-row gap-5">
              {n.hostel_image && (
                <img src={n.hostel_image} className="w-full md:w-40 h-32 md:h-24 object-cover rounded-xl" alt="" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="text-xs tracking-widest uppercase font-bold text-emerald-700">
                    Booking confirmed
                  </span>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-amber-600" />}
                </div>
                <div className="mt-1 font-display font-bold text-xl">{n.hostel_name}</div>
                <div className="text-xs text-slate-500">
                  {n.check_in} → {n.check_out} · Paid ₹{(n.amount || 0).toLocaleString("en-IN")}
                </div>

                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {n.owner_phone && (
                    <button
                      data-testid={`inbox-copy-phone-${n.id}`}
                      onClick={() => copy(n.owner_phone, "Owner phone")}
                      className="text-left bg-slate-50 border border-slate-200 rounded-xl p-3 hover:border-orange-600 transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500 flex items-center gap-1">
                          <Phone size={10} /> Owner phone
                        </div>
                        <Copy size={12} className="text-slate-400 group-hover:text-orange-800" />
                      </div>
                      <div className="mt-1 font-semibold">{n.owner_name}</div>
                      <div className="text-sm font-mono">{n.owner_phone}</div>
                    </button>
                  )}
                  {n.location && (
                    <a
                      href={n.maps_link || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`inbox-map-${n.id}`}
                      className="text-left bg-slate-50 border border-slate-200 rounded-xl p-3 hover:border-orange-600 transition-colors group block"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500 flex items-center gap-1">
                          <MapPin size={10} /> Location
                        </div>
                        <ExternalLink size={12} className="text-slate-400 group-hover:text-orange-800" />
                      </div>
                      <div className="mt-1 text-sm font-semibold line-clamp-2">{n.location}</div>
                      <div className="text-xs text-orange-800 mt-1">Open in Google Maps →</div>
                    </a>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {n.whatsapp_owner_link && (
                    <a href={n.whatsapp_owner_link} target="_blank" rel="noopener noreferrer"
                      data-testid={`inbox-wa-owner-${n.id}`}
                      className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors">
                      <MessageCircle size={14} /> WhatsApp owner
                    </a>
                  )}
                  {n.whatsapp_self_link && (
                    <a href={n.whatsapp_self_link} target="_blank" rel="noopener noreferrer"
                      data-testid={`inbox-wa-self-${n.id}`}
                      className="inline-flex items-center gap-2 bg-white border border-emerald-500 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold px-4 py-2 rounded-full transition-colors">
                      <MessageCircle size={14} /> Send to my WhatsApp
                    </a>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <a
                    href={`mailto:${SUPPORT_EMAIL}?subject=Help%20with%20booking%20${n.booking_id?.slice(0, 8)}`}
                    className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-orange-800"
                  >
                    <Mail size={12} /> Need help? {SUPPORT_EMAIL}
                  </a>
                  {!n.read && (
                    <button
                      onClick={() => markRead(n.id)}
                      className="text-xs text-orange-800 font-semibold hover:underline"
                    >
                      Mark as read
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
